/**
 * useMyDocuments Hook
 *
 * Fetches customer's documents with pagination support.
 * Uses the getMyDocuments query from the CRM service.
 *
 * Requirements: Task 10 - Documents Page
 */

import { useMemo, useCallback } from 'react';
import {
  useGetMyDocumentsQuery,
  type GetMyDocumentsQuery,
  type PaginationInput,
} from '@bloomcode/graphql';

// Type aliases for query results
type DocumentItem = GetMyDocumentsQuery['getMyDocuments']['data'][number];
type PaginationInfo = GetMyDocumentsQuery['getMyDocuments']['pagination'];

interface UseMyDocumentsOptions {
  page?: number;
  limit?: number;
  skip?: boolean;
}

interface UseMyDocumentsResult {
  documents: DocumentItem[];
  pagination: PaginationInfo | null;
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch the current customer's documents with pagination
 *
 * @param options - Configuration options
 * @returns Customer documents data with loading and error states
 */
export function useMyDocuments(options: UseMyDocumentsOptions = {}): UseMyDocumentsResult {
  const { page = 1, limit = 10, skip = false } = options;

  const pagination: PaginationInput = { page, limit };

  const {
    data,
    loading,
    error,
    refetch: refetchQuery,
  } = useGetMyDocumentsQuery({
    variables: { pagination },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const documents: DocumentItem[] = useMemo(() => data?.getMyDocuments?.data ?? [], [data]);

  const paginationInfo: PaginationInfo | null = useMemo(
    () => data?.getMyDocuments?.pagination ?? null,
    [data]
  );

  const totalCount = paginationInfo?.total ?? 0;

  const refetch = useCallback(() => {
    refetchQuery();
  }, [refetchQuery]);

  return {
    documents,
    pagination: paginationInfo,
    totalCount,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

export type { DocumentItem, PaginationInfo as DocumentPaginationInfo };
export default useMyDocuments;
